/**
 * Hook som kör simulate() i en Web Worker.
 *
 * Hanterar:
 * - Skapa/återanvända en worker över komponentens livstid
 * - requestId-spårning så bara senaste simulering visas (slider-spam-skydd)
 * - Cleanup på unmount
 * - Fallback till sync simulate() om Worker inte stöds (gamla webbläsare,
 *   SSR, eller test-miljö)
 */

import { useEffect, useRef, useState } from "react";
import { simulate } from "../sim/simulate";
import type { HourPoint, Scenario, SimulationResult } from "../sim/types";
import type {
  SimWorkerRequest,
  SimWorkerResponse,
} from "../sim/sim.worker";

interface UseSimulationResult {
  result: SimulationResult | null;
  isComputing: boolean;
}

const supportsWorker = typeof Worker !== "undefined";

export function useSimulation(
  hours: HourPoint[],
  scenario: Scenario,
  ready: boolean,
): UseSimulationResult {
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [isComputing, setIsComputing] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const nextRequestId = useRef(0);
  // Senaste requestId vi skickat — bara svar med detta id renderas
  const latestRequestId = useRef(0);

  useEffect(() => {
    if (!supportsWorker) return;

    // Lazy: skapa worker första gången
    if (!workerRef.current) {
      try {
        workerRef.current = new Worker(
          new URL("../sim/sim.worker.ts", import.meta.url),
          { type: "module" },
        );
        workerRef.current.onmessage = (
          e: MessageEvent<SimWorkerResponse>,
        ) => {
          // Ignorera svar för gamla requests (slider rörde sig vidare)
          if (e.data.requestId !== latestRequestId.current) return;
          setIsComputing(false);
          if ("error" in e.data) {
            // Behåll förra resultatet vid fel
            console.error("Sim worker error:", e.data.error);
            return;
          }
          setResult(e.data.result);
        };
      } catch (err) {
        console.error("Worker init failed, falling back to main thread:", err);
        workerRef.current = null;
      }
    }

    return () => {
      // Worker återanvänds över hela komponentens livstid. Bara terminera
      // vid unmount.
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!ready || hours.length < 24) {
      setResult(null);
      return;
    }

    // Fallback: kör sync på main thread om Worker inte fungerar
    if (!workerRef.current) {
      try {
        const r = simulate(hours, scenario);
        setResult(r);
      } catch (err) {
        console.error("Sync sim failed:", err);
        setResult(null);
      }
      return;
    }

    const requestId = ++nextRequestId.current;
    latestRequestId.current = requestId;
    setIsComputing(true);
    const request: SimWorkerRequest = { requestId, hours, scenario };
    workerRef.current.postMessage(request);
  }, [hours, scenario, ready]);

  return { result, isComputing };
}
